import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { buildConsentView, type ConsentView } from './consentModel';
import type { ConsentRecord, User } from '../dataProvider/types';

/**
 * A member's consent records, read through the aggregation seam (`buildConsentView`), NOT
 * computed in the component. It pulls the subject and their consent history from the mock
 * provider and derives the standing consent per type on read. The real-data migration replaces
 * the fetch-and-aggregate with a server-side endpoint and the surface does not change. Same seam
 * idiom as useDsarExport / useMemberEyeView.
 *
 * `refreshKey` lets the surface force a refetch after the one narrow withdrawal write appends a
 * record, so the derived current state reflects the new history.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
};

export const useConsent = (
  subjectUserId: number,
  refreshKey = 0,
): { view: ConsentView | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['consent', subjectUserId, refreshKey],
    queryFn: async () => {
      const user = (await dataProvider.getOne('users', { id: subjectUserId }))
        .data as unknown as User;
      const records = await dataProvider.getList('consents', {
        ...ALL,
        filter: { userId: subjectUserId },
      });
      return buildConsentView({
        user,
        records: records.data as unknown as ConsentRecord[],
      });
    },
  });

  return { view: data ?? null, isLoading };
};
